/* Lumetier AI Copilot — floating pill + slide-in chat panel.

   Lives in `<AdminLayout>` so it shows on every admin page. The pill sits
   bottom-right (Pixieset-style); clicking it opens a 480px-wide drawer from
   the right (full-screen on mobile). One conversation at a time for v1 —
   conversation switcher / history list can come in v2. */

import { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send, Loader2, AlertTriangle, RefreshCw, Plus, Maximize2, Minimize2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Summarize this week's bookings",
  "Any overdue invoices?",
  "Draft a warm reminder for a pending deposit",
  "Galleries that haven't been delivered yet",
];

export default function CopilotPill({ forceOpen = false, onClose }) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen || open;

  function handleClose() {
    setOpen(false);
    onClose?.();
  }

  return (
    <>
      {/* Floating pill — desktop only. On mobile (<lg) the bottom tab bar
          surfaces the AI Copilot instead. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="copilot-open-pill"
        aria-label="Open Lumetier AI Copilot"
        className={`hidden lg:inline-flex fixed bottom-6 right-6 z-40 group items-center gap-2 pl-4 pr-5 h-12 rounded-full border border-[#C9A961]/50 bg-gradient-to-br from-[#1a1916] to-[#0e0d0c] text-[#E8D9B5] hover:border-[#C9A961] hover:shadow-[0_8px_30px_rgb(201,169,97,0.18)] transition-all shadow-lg ${
          isOpen ? "opacity-0 pointer-events-none scale-90" : "opacity-100 scale-100"
        }`}
      >
        <Sparkles className="w-4 h-4 text-[#C9A961] group-hover:scale-110 transition-transform" />
        <span className="text-xs uppercase tracking-[0.24em] font-medium">Ask Lumetier</span>
      </button>

      {/* Slide-in panel — same instance for both desktop pill + mobile tab */}
      {isOpen && <CopilotPanel onClose={handleClose} />}
    </>
  );
}

function CopilotPanel({ onClose }) {
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);   // [{id, role, content, created_at}]
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [usage, setUsage] = useState({ daily_used: 0, daily_limit: 50 });
  const [error, setError] = useState("");
  const [maximized, setMaximized] = useState(() => {
    try { return localStorage.getItem("copilot_maximized") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("copilot_maximized", maximized ? "1" : "0"); } catch { /* ignore */ }
  }, [maximized]);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  // Fetch today's usage on mount so the footer shows accurate "X/50" right away.
  useEffect(() => {
    let live = true;
    api.get("/admin/ai/usage").then(({ data }) => { if (live) setUsage(data); }).catch(() => {});
    return () => { live = false; };
  }, []);

  // Auto-scroll on every new message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Autofocus the input on open.
  useEffect(() => { inputRef.current?.focus(); }, []);

  async function sendMessage(text) {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;

    setError("");
    setSending(true);

    // Optimistic user-bubble — we render it instantly so the chat feels fast
    // even before the LLM round-trip completes.
    const optimisticId = `tmp_${Date.now()}`;
    const optimisticMsg = { id: optimisticId, role: "user", content: trimmed, pending: true };
    setMessages((prev) => [...prev, optimisticMsg]);
    setInput("");

    try {
      const { data } = await api.post("/admin/ai/chat", {
        conversation_id: conversationId,
        message: trimmed,
      });
      // Replace optimistic with the real persisted message, then append assistant.
      setMessages((prev) => {
        const withoutOpt = prev.filter((m) => m.id !== optimisticId);
        return [...withoutOpt, data.user_message, data.assistant_message];
      });
      setConversationId(data.conversation_id);
      setUsage({ daily_used: data.daily_used, daily_limit: data.daily_limit });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not reach the AI service. Try again in a moment.";
      setError(msg);
      // Roll back the optimistic bubble on failure.
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      if (err.response?.status === 429) {
        toast.error(msg);
      }
    } finally {
      setSending(false);
      // Refocus so the photographer can immediately type a follow-up.
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }

  function startFresh() {
    setConversationId(null);
    setMessages([]);
    setError("");
    setInput("");
    setTimeout(() => inputRef.current?.focus(), 30);
  }

  const quotaPct = Math.min(100, Math.round((usage.daily_used / usage.daily_limit) * 100));
  const overLimit = usage.daily_used >= usage.daily_limit;

  return (
    <>
      {/* Floating panel — anchored bottom-right, ~420×600. Maximize toggle in
          the header expands to the previous side-sheet for power users; the
          preference persists in localStorage. On mobile (<sm), the panel goes
          to a full-width bottom sheet (max 80vh). */}
      <div
        className={`fixed z-50 text-stone-200 bg-gradient-to-b from-[#0f0e0c] via-[#0c0b0a] to-[#0a0908] border border-[#C9A961]/25 shadow-[0_28px_90px_-15px_rgba(0,0,0,0.85)] flex flex-col backdrop-blur-md ${
          maximized
            ? "top-0 right-0 h-full w-full sm:w-[480px] border-l border-r-0 rounded-none animate-in slide-in-from-right duration-300"
            : "right-6 bottom-[88px] w-[calc(100vw-32px)] sm:w-[420px] max-h-[80vh] sm:h-[600px] rounded-2xl animate-in fade-in slide-in-from-bottom-2 duration-200"
        }`}
        data-testid="copilot-panel"
        data-maximized={maximized ? "true" : "false"}
        role="dialog"
        aria-label="Lumetier AI Copilot"
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <header className="px-6 py-5 border-b border-white/[0.06] flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A961]/30 to-[#C9A961]/5 border border-[#C9A961]/40 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#C9A961]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] text-stone-500 font-sans">
              Lumetier
            </div>
            <div className="font-serif text-xl text-stone-100 tracking-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              AI Copilot
            </div>
          </div>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={startFresh}
              data-testid="copilot-new-chat"
              title="Start a new conversation"
              className="w-9 h-9 rounded-full border border-white/10 hover:border-white/30 text-stone-400 hover:text-stone-100 transition-colors inline-flex items-center justify-center"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setMaximized((v) => !v)}
            data-testid="copilot-maximize"
            aria-label={maximized ? "Restore to floating panel" : "Maximize to side sheet"}
            title={maximized ? "Restore" : "Maximize"}
            className="w-9 h-9 rounded-full border border-white/10 hover:border-[#C9A961]/60 text-stone-400 hover:text-[#C9A961] transition-colors inline-flex items-center justify-center"
          >
            {maximized
              ? <Minimize2 className="w-3.5 h-3.5" />
              : <Maximize2 className="w-3.5 h-3.5" />
            }
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="copilot-close"
            aria-label="Close Copilot"
            className="w-9 h-9 rounded-full border border-white/10 hover:border-white/30 text-stone-400 hover:text-stone-100 transition-colors inline-flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* ── Messages ───────────────────────────────────────────── */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-6 py-6 space-y-6"
          data-testid="copilot-message-list"
        >
          {messages.length === 0 && !sending ? (
            <EmptyState onSuggestionClick={(s) => sendMessage(s)} />
          ) : (
            messages.map((m) => <ChatBubble key={m.id} message={m} />)
          )}
          {sending && <ThinkingBubble />}
        </div>

        {/* ── Error banner ───────────────────────────────────────── */}
        {error && (
          <div className="px-6 py-3 border-t border-red-500/20 bg-red-950/30 flex items-start gap-2 text-xs text-red-300">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError("")} className="text-red-300/70 hover:text-red-200">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* ── Composer ───────────────────────────────────────────── */}
        <form
          onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
          className="px-6 py-4 border-t border-white/[0.06] bg-[#0a0908]"
        >
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              disabled={sending || overLimit}
              placeholder={overLimit ? "Daily limit reached — resets at midnight UTC" : "Ask anything about your studio…"}
              rows={1}
              maxLength={4000}
              data-testid="copilot-input"
              className="flex-1 bg-white/[0.04] border border-white/[0.08] focus:border-[#C9A961]/60 focus:outline-none rounded-md px-3 py-2.5 text-sm text-stone-100 placeholder:text-stone-600 resize-none max-h-32 transition-colors disabled:opacity-50"
              style={{ minHeight: "42px" }}
            />
            <button
              type="submit"
              disabled={!input.trim() || sending || overLimit}
              data-testid="copilot-send"
              className="w-10 h-10 shrink-0 rounded-md bg-[#C9A961] text-stone-900 hover:opacity-90 disabled:opacity-30 inline-flex items-center justify-center transition-opacity"
              aria-label="Send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>

          {/* Footer — daily usage meter */}
          <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.22em]">
            <div className="text-stone-600">
              Claude Sonnet 4.6 · scoped to your studio
            </div>
            <div className="flex items-center gap-2 text-stone-500">
              <span className="tabular-nums">{usage.daily_used}/{usage.daily_limit}</span>
              <div className="w-12 h-1 bg-white/[0.06] rounded-full overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${quotaPct}%`,
                    background: quotaPct > 85 ? "#dc2626" : "#C9A961",
                  }}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

function ChatBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`copilot-msg-${message.role}`}>
      <div
        className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#C9A961] text-stone-900 rounded-2xl rounded-br-sm font-medium"
            : "bg-white/[0.04] border border-white/[0.06] text-stone-200 rounded-2xl rounded-bl-sm"
        } ${message.pending ? "opacity-60" : ""}`}
      >
        {message.content}
      </div>
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div className="flex justify-start" data-testid="copilot-thinking">
      <div className="bg-white/[0.04] border border-white/[0.06] text-stone-400 rounded-2xl rounded-bl-sm px-4 py-3 inline-flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[#C9A961] animate-pulse" style={{ animationDelay: "0ms" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-[#C9A961] animate-pulse" style={{ animationDelay: "150ms" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-[#C9A961] animate-pulse" style={{ animationDelay: "300ms" }} />
        <span className="text-[11px] uppercase tracking-[0.24em] text-stone-500 ml-1">Thinking</span>
      </div>
    </div>
  );
}

function EmptyState({ onSuggestionClick }) {
  return (
    <div className="text-center py-8" data-testid="copilot-empty-state">
      <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-gradient-to-br from-[#C9A961]/30 to-[#C9A961]/5 border border-[#C9A961]/30 flex items-center justify-center">
        <Sparkles className="w-5 h-5 text-[#C9A961]" />
      </div>
      <h2
        className="text-3xl font-light text-stone-100 mb-3"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        How can I help today?
      </h2>
      <p className="text-xs text-stone-500 max-w-xs mx-auto leading-relaxed mb-8">
        I can answer questions about your bookings, draft emails, summarize your week, or help write client messages.
      </p>
      <div className="space-y-2.5 max-w-sm mx-auto">
        {SUGGESTIONS.map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onSuggestionClick(s)}
            data-testid={`copilot-suggestion-${i}`}
            className="w-full text-left text-sm text-stone-300 hover:text-stone-100 bg-white/[0.025] hover:bg-white/[0.05] border border-white/[0.06] hover:border-[#C9A961]/40 rounded-lg px-4 py-3 transition-all group"
          >
            <span className="inline-block w-1 h-1 rounded-full bg-[#C9A961] mr-3 align-middle" />
            {s}
            <span className="float-right text-stone-600 group-hover:text-[#C9A961] transition-colors">→</span>
          </button>
        ))}
      </div>
    </div>
  );
}
